import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faEllipsis, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';

import { namedOperations } from '@sorare/core/src/__generated__/operations';
import type { Props as ButtonProps } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import {
  Dropdown,
  DropdownOptionLabel,
} from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { AddToWatchlistIcon } from '@sorare/core/src/atoms/icons/AddToWatchlistIcon';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useToggleSubscription } from '@sorare/core/src/hooks/subscriptions/useToggleSubscription';
import { glossary } from '@sorare/core/src/lib/glossary';

import { AddToWatchlist } from '#src/components/watchlists/AddToWatchlist/index.tsx';
import { useRemoveFromWatchlist } from '#src/hooks/watchlist/useRemoveFromWatchlist.ts';

import type { ManagePlayerInWatchlist_anyPlayer } from './__generated__/index.graphql.ts';

type Props = {
  anyPlayer: ManagePlayerInWatchlist_anyPlayer;
  watchlistId?: string;
} & ButtonProps;

export const ManagePlayerInWatchlist = ({
  anyPlayer,
  watchlistId,
  ...props
}: Props) => {
  const [open, setOpen] = useState(false);
  const [openAddToWatchlist, setOpenAddToWatchlist] = useState(false);
  const { removeFromWatchlist } = useRemoveFromWatchlist();
  const { refresh } = useInstantSearch() || {};

  const toggleSubscription = useToggleSubscription(
    anyPlayer.currentUserSubscription,
    anyPlayer,
    undefined,
    [namedOperations.Query.FavoritesWatchlistPageQuery]
  );

  const onRemoveFromWatchlist = () => {
    (watchlistId
      ? removeFromWatchlist.mutate(watchlistId, [
          anyPlayer.commonPlayer?.id || '',
        ])
      : toggleSubscription()
    ).then(() => {
      setOpen(false);
      setOpenAddToWatchlist(false);
      refresh?.();
    });
  };

  return (
    <Dropdown
      gap={8}
      open={open}
      onClose={() => {
        setOpen(false);
        setOpenAddToWatchlist(false);
      }}
      label={p => (
        <Button {...props} {...p} onClick={() => setOpen(true)}>
          <FontAwesomeIcon icon={faEllipsis} />
        </Button>
      )}
    >
      {openAddToWatchlist ? (
        <AddToWatchlist anyPlayer={anyPlayer} onClose={() => setOpen(false)} />
      ) : (
        <Vertical>
          <DropdownOptionLabel onClick={() => setOpenAddToWatchlist(true)}>
            <AddToWatchlistIcon style={{ color: 'var(--c-white)' }} />
            <BodyM color="var(--c-white)" bold>
              <FormattedMessage defaultMessage="Add to" />
            </BodyM>
          </DropdownOptionLabel>
          <DropdownOptionLabel onClick={onRemoveFromWatchlist}>
            <FontAwesomeIcon color="var(--c-red-400)" icon={faTrash} />
            <BodyM color="var(--c-red-400)" bold>
              <FormattedMessage {...glossary.remove} />
            </BodyM>
          </DropdownOptionLabel>
        </Vertical>
      )}
    </Dropdown>
  );
};

ManagePlayerInWatchlist.fragments = {
  anyPlayer: gql`
    fragment ManagePlayerInWatchlist_anyPlayer on AnyPlayerInterface {
      slug
      currentUserSubscription {
        id
        slug
      }
      commonPlayer(rarity: common) {
        id
      }
      ...AddToWatchlist_anyPlayer
    }
    ${AddToWatchlist.fragments.anyPlayer}
  ` as TypedDocumentNode<ManagePlayerInWatchlist_anyPlayer>,
};
